import { useEffect, useState, useCallback } from "react";
import { Share, Plus, X, Smartphone } from "lucide-react";

/**
 * Smart "Add Routa to Home Screen" prompt for driver phones.
 *
 * Behavior:
 *   • Android / desktop Chromium: intercepts `beforeinstallprompt`, shows a
 *     one-tap "Install" button, and dismisses on user choice.
 *   • iOS Safari (no programmatic install): shows a coach-mark with the
 *     "Tap Share → Add to Home Screen" steps.
 *   • Hidden if already running in standalone mode (installed) or after the
 *     user has dismissed the banner (persisted per-user in localStorage).
 */
const DISMISS_KEY = "routa_pwa_dismissed_v1";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPhone|iPad|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
}

function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || "";
  // Safari on iOS, not Chrome ("CriOS") or Firefox ("FxiOS")
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(true); // start hidden; enable in effect
  const [iosCoach, setIosCoach] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    setDismissed(false);

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => {
      localStorage.setItem(DISMISS_KEY, "1");
      setDismissed(true);
    };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
    setIosCoach(false);
  }, []);

  const install = useCallback(async () => {
    if (deferred) {
      deferred.prompt();
      try {
        const choice = await deferred.userChoice;
        if (choice?.outcome === "accepted") {
          dismiss();
        }
      } catch { /* user cancelled */ }
      setDeferred(null);
      return;
    }
    // iOS Safari — cannot trigger install, show the coach-mark.
    if (isIOSSafari()) setIosCoach(true);
  }, [deferred, dismiss]);

  if (dismissed) return null;

  // Only show the actionable button when we can help the user install.
  const showInstallable = !!deferred || isIOSSafari();
  if (!showInstallable) return null;

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="mb-6 border border-gray-200 bg-gray-50 px-4 py-3 flex items-center gap-3 lg:mb-8"
      >
        <div className="grid place-items-center h-10 w-10 shrink-0 border border-gray-200 bg-white">
          <Smartphone className="h-5 w-5 text-orange-500" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-gray-400">Faster on the road</div>
          <div className="text-sm text-[#0a0a0a] mt-0.5 truncate">Add Routa to your home screen for one-tap access.</div>
        </div>
        <button
          onClick={install}
          data-testid="pwa-install-button"
          className="shrink-0 px-3.5 py-2 text-[12px] font-semibold bg-[#0a0a0a] text-white hover:bg-orange-500 transition-colors"
        >
          {deferred ? "Install" : "How to install"}
        </button>
        <button
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          className="shrink-0 p-1 text-gray-400 hover:text-[#0a0a0a]"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {iosCoach && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          data-testid="pwa-ios-coach"
          onClick={() => setIosCoach(false)}
        >
          <div className="bg-white w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIosCoach(false)}
              aria-label="Close"
              className="absolute top-3 right-3 p-1 text-gray-400 hover:text-[#0a0a0a]"
              data-testid="pwa-ios-close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 mb-3">[ROUTA · INSTALL]</div>
            <h2 className="text-2xl font-bold tracking-tight text-[#0a0a0a] mb-4">Add Routa to your home screen</h2>
            <ol className="space-y-4 text-sm text-[#0a0a0a]">
              <li className="flex items-start gap-3">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-[#0a0a0a] text-white font-mono text-[11px] shrink-0">1</span>
                <span>Tap the <Share className="inline h-4 w-4 -mt-0.5 mx-1 text-orange-500" /> <b>Share</b> icon in Safari's toolbar.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-[#0a0a0a] text-white font-mono text-[11px] shrink-0">2</span>
                <span>Scroll down and choose <Plus className="inline h-4 w-4 -mt-0.5 mx-1 text-orange-500" /> <b>Add to Home Screen</b>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-[#0a0a0a] text-white font-mono text-[11px] shrink-0">3</span>
                <span>Tap <b>Add</b> — Routa will launch from your home screen just like a native app.</span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              data-testid="pwa-ios-got-it"
              className="mt-6 w-full px-6 py-3 text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
